// Sign-in Map export — CSV/HTML built client-side (embedded base64 logo, Analytics
// styling); PDF is a real file generated by the backend ReportLab builder.
import { format } from 'date-fns';
import { toast } from 'sonner';
import { loadLogoPng } from './tripExport';
import { formatDT, getUserTimezone } from './timezone';
import { formatUTCStamp } from './dateFormat';

let __logoCache = null;
const getLogo = async () => {
  if (__logoCache) return __logoCache;
  try {
    const logo = await loadLogoPng();
    if (logo?.dataUrl) __logoCache = logo.dataUrl;
  } catch { /* export continues without logo */ }
  return __logoCache || '';
};

const csvVal = (v) => {
  const s = String(v ?? '');
  if (/^\d{16,}$/.test(s)) return `="${s}"`;
  return `"${s.replace(/"/g, '""')}"`;
};

const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const buildExportHTML = (title, subtitle, bodyHTML, exportTimestamp, logoSrc) => `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${title}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif;background:#fff;color:#1e293b}
.header{height:60px;background:#fff;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;padding:0 16px;gap:16px}
.header img{height:36px}.header-info{font-size:13px;color:#475569}.header-info b{color:#1e293b}
.content{padding:20px}table{width:100%;border-collapse:collapse;font-size:11px;margin-top:12px}
th{background:#f1f5f9;color:#475569;font-weight:600;text-align:left;padding:6px 8px;border:1px solid #e2e8f0;white-space:nowrap}
td{padding:5px 8px;border:1px solid #e2e8f0;vertical-align:top}tr:nth-child(even){background:#f8fafc}
a{color:#2563eb;text-decoration:none}a:hover{text-decoration:underline}
.sus{color:#dc2626;font-weight:600}
.meta{margin-bottom:12px;font-size:12px;color:#64748b}.meta b{color:#1e293b}
.footer{margin-top:20px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#475569;text-align:center}
@media print{.header{position:fixed;top:0;left:0;right:0;border-bottom:2px solid #2563eb}.content{margin-top:70px}@page{margin:10mm 8mm}}</style></head><body>
<div class="header">${logoSrc ? `<img src="${logoSrc}" alt="EnsueLIVETRAK"/>` : ''}<div class="header-info"><b>${title}</b>${subtitle ? ' &mdash; ' + subtitle : ''}</div></div>
<div class="content">${bodyHTML}<div class="footer">Generated by EnsueLIVETRAK &mdash; ${exportTimestamp}</div></div></body></html>`;

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const exportSigninMap = async ({ points, days, fmt, api }) => {
  if (!points?.length) { toast.error('No sign-in data to export'); return; }
  const baseFn = `signin_map_${days}d_${format(new Date(), 'yyyyMMdd_HHmm')}`;
  if (fmt === 'pdf') {
    // Real PDF file from the backend (ReportLab) — same as Trips/Trip Points/Messages.
    try {
      toast.info('Generating PDF...');
      const tz = getUserTimezone() || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const res = await api.get(`/security/login-map/export/pdf?days=${days}&tz=${encodeURIComponent(tz)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFn}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success('Sign-in map PDF downloaded');
    } catch (e) {
      toast.error(e?.response?.status === 404 ? 'No sign-in data for this period' : 'PDF export failed');
    }
    return;
  }
  const headers = ['Location', 'IP Address', 'Sign-ins', 'Last Sign-in (Local)', 'Last Sign-in (UTC)', 'Accounts', 'Suspicious', 'Latitude', 'Longitude', 'Map Link'];
  const rows = points.map((p) => [
    p.location || 'Unknown',
    p.ip,
    p.count,
    p.last_at ? formatDT(p.last_at, { seconds: true }) : '',
    p.last_at ? formatUTCStamp(p.last_at, { seconds: true }) : '',
    (p.users || []).join('; '),
    p.suspicious ? 'Yes' : 'No',
    p.lat ?? '',
    p.lng ?? '',
    p.lat != null ? `https://www.google.com/maps?q=${p.lat},${p.lng}` : '',
  ]);
  if (fmt === 'csv') {
    downloadFile([headers.map(csvVal).join(','), ...rows.map((r) => r.map(csvVal).join(','))].join('\n'), `${baseFn}.csv`, 'text/csv');
    toast.success('Sign-in map exported');
    return;
  }
  const exportTs = formatDT(new Date().toISOString(), { seconds: true });
  const logoSrc = await getLogo();
  const suspicious = points.filter((p) => p.suspicious).length;
  const tbl = `<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map((r, i) => {
    const p = points[i];
    return `<tr>${r.map((v, ci) => {
      if (ci === 0 && p.lat != null) return `<td><a href="https://www.google.com/maps?q=${p.lat},${p.lng}" target="_blank">${esc(v)}</a></td>`;
      if (ci === 6 && v === 'Yes') return `<td class="sus">Yes</td>`;
      if (ci === 9) return `<td>${v ? `<a href="${v}" target="_blank">Open map</a>` : ''}</td>`;
      return `<td>${esc(v)}</td>`;
    }).join('')}</tr>`;
  }).join('')}</tbody></table>`;
  const html = buildExportHTML(
    'Sign-in Map',
    `Last ${days} days • ${points.length} IP address${points.length === 1 ? '' : 'es'}`,
    `<div class="meta"><b>Period:</b> last ${days} days • <b>IP addresses:</b> ${points.length} • <b>With suspicious sign-ins:</b> ${suspicious}</div>${tbl}`,
    exportTs, logoSrc,
  );
  downloadFile(html, `${baseFn}.html`, 'text/html');
  toast.success('Sign-in map exported as HTML');
};
